import { useState, useLayoutEffect, Fragment } from 'react';
import ServiceDirectoryMenu from '../../components/ServiceDirectoryMenu/ServiceDirectoryMenu';
import TextInformation from '../../components/TextInformation/TextInformation';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  BodyText,
  FairBodyText,
  Row,
  SmallHeightDivider,
  RowBodyDivider,
  MediumHeightDivider,
} from '../../theme/Styles';
import { Grid, Container } from '@mui/material';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import { useHistory, useParams } from 'react-router';
import LoginOrRegisterModal from '../../components/LoginOrRegisterModal/LoginOrRegisterModal';
import { useDispatch, useSelector } from 'react-redux';
import { UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import { useTranslation } from 'react-i18next';
import { getAlcaldiaTramites } from '../../api/Alcaldia';
import { useQuery } from 'react-query';
import CenterLoading from '../../components/CenterLoading/CenterLoading';

function AlcaldiaView() {
  const matchesWidth = useMediaQuery('(min-width:768px)');
  const history = useHistory();
  let { alcaldiaId } = useParams();
  const dispatch = useDispatch();
  const { authenticated } = useSelector((state) => state.authReducer);

  const { data: alcaldiaData, isLoading: alcaldiaLoading } = useQuery(
    ['alcaldiaTramites', alcaldiaId],
    () => getAlcaldiaTramites(alcaldiaId),
    { staleTime: 0, cacheTime: 0 }
  );

  const [t] = useTranslation('Service');
  const [loginOrRegisterModalStatus, setLoginOrRegisterModalStatus] = useState(false);

  const handleServiceRequest = (serviceID) => {
    if (authenticated) {
      history.push(`/app/requestService/${serviceID}`);
    } else {
      setLoginOrRegisterModalStatus(!loginOrRegisterModalStatus);
    }
  };

  useLayoutEffect(() => {
    if (alcaldiaData?.alcaldia) {
      dispatch(UpdateAppSubHeaderTitle(alcaldiaData.alcaldia.name || 'Ayuntamiento'));
    }
  }, [alcaldiaData, dispatch]);

  if (alcaldiaLoading) return <CenterLoading />;

  const alcaldia = alcaldiaData?.alcaldia;

  return (
    <Container>
      <LoginOrRegisterModal
        open={loginOrRegisterModalStatus}
        onBackDropClick={() => setLoginOrRegisterModalStatus(false)}
        onCloseClick={() => setLoginOrRegisterModalStatus(false)}
      />
      <Row>
        {matchesWidth && (
          <Fragment>
            <ServiceDirectoryMenu />
            <RowBodyDivider />
          </Fragment>
        )}
        <Container style={{ width: '100%' }}>

          {/* Municipio — equivalente a "Institución" */}
          {alcaldia?.municipality && (
            <Fragment>
              <TextInformation title='Municipio' content={alcaldia.municipality} />
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Descripción general */}
          {alcaldia?.description && (
            <Fragment>
              <TextInformation title='Información General' content={alcaldia.description} />
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Dirección */}
          {alcaldia?.address && (
            <Fragment>
              <TextInformation title='Dirección' content={alcaldia.address} />
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Información de contacto */}
          {(alcaldia?.phone || alcaldia?.email || alcaldia?.website) && (
            <Fragment>
              <TextInformation title='Contacto' />
              <SmallHeightDivider />
              {alcaldia?.phone && (
                <Row style={{ marginBottom: '4px' }}>
                  <strong><BodyText>Teléfono:&nbsp;</BodyText></strong>
                  <BodyText>{alcaldia.phone}</BodyText>
                </Row>
              )}
              {alcaldia?.email && (
                <Row style={{ marginBottom: '4px' }}>
                  <strong><BodyText>Email:&nbsp;</BodyText></strong>
                  <BodyText>{alcaldia.email}</BodyText>
                </Row>
              )}
              {alcaldia?.website && (
                <Row style={{ marginBottom: '4px' }}>
                  <strong><BodyText>Sitio web:&nbsp;</BodyText></strong>
                  <BodyText
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => window.open(alcaldia.website, '_blank')}
                  >
                    {alcaldia.website}
                  </BodyText>
                </Row>
              )}
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Horario — equivalente a "Horario de prestación de servicio" */}
          {alcaldia?.schedule && (
            <Fragment>
              <TextInformation
                title='Horario de atención'
                content={alcaldia.schedule}
              />
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Requisitos */}
          {alcaldia?.requirements && (
            <Fragment>
              <TextInformation title='Requisitos' />
              <SmallHeightDivider />
              <ul style={{ paddingLeft: '20px', margin: '0' }}>
                {alcaldia.requirements.split('\n').filter(r => r.trim()).map((req, index) => (
                  <li key={index} style={{ marginTop: '5px' }}>
                    <FairBodyText>{req.trim()}</FairBodyText>
                  </li>
                ))}
              </ul>
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Procedimiento */}
          {alcaldia?.procedures && (
            <Fragment>
              <TextInformation title='Procedimiento' />
              <SmallHeightDivider />
              <ol style={{ paddingLeft: '20px', margin: '0' }}>
                {alcaldia.procedures.split('\n').filter(p => p.trim()).map((step, index) => (
                  <li key={index} style={{ marginTop: '5px' }}>
                    <BodyText>{step.trim()}</BodyText>
                  </li>
                ))}
              </ol>
              <SmallHeightDivider />
            </Fragment>
          )}

          {/* Tarifa */}
          {alcaldia?.price_info && (
            <Fragment>
              <TextInformation title='Tarifa del servicio' content={alcaldia.price_info} />
              <SmallHeightDivider />
            </Fragment>
          )}

          <SmallHeightDivider />

          {/* Lista de trámites */}
          <TextInformation title={t('Services')} />
          <SmallHeightDivider />

          {alcaldiaData?.tramites?.length > 0 ? (
            <Grid
              alignItems='center'
              container
              direction='row'
              justifyContent={!matchesWidth ? 'center' : 'flex-start'}
              spacing={{ xs: 2, md: 3 }}
              columns={{ xs: 4, sm: 8, md: 12 }}
            >
              {alcaldiaData.tramites.map((item) => (
                <Grid key={item.id} item>
                  <ServiceCard
                    itemId={item.id}
                    title={item?.name}
                    subTitle={item.description}
                    relationTo='ayuntamientos'
                    imageUrl={(item?.image && item?.image.trim() !== '') ? item?.image : (item?.icon && item?.icon.trim() !== '') ? item?.icon : null}
                    iconName={item?.icon_name}
                    onRequestPress={() => handleServiceRequest(item.id)}
                    OnViewInformationPress={() => history.push(`/app/serviceDescription/${item.id}`)}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <BodyText>No hay trámites disponibles para esta alcaldía en este momento.</BodyText>
          )}

          <MediumHeightDivider />
        </Container>
      </Row>
    </Container>
  );
}

export default AlcaldiaView;
